/**
 * Url normalization and validation for extension dialogs.
 * This class implements the same functionality as ExtensionUtils.cpp
 */
export namespace ExtensionUtils {
  function isRelative(url: string): boolean {
    const regExp = new RegExp('^([a-z]+:)?//', 'i');
    return !regExp.test(url);
  }

  function isSandboxedExtensionUrl(url: URL): boolean {
    // Yes, this can give a false positive if a developer uses http://localhost/sandbox/... manually,
    // but the only drawback is they need to use a relative URL to their dialog.

    const localHost = 'localhost';
    const tableauHost = 'extensions.tableauusercontent.com';
    const sandboxPathSegment = '/sandbox/';

    const host: string = url.hostname;
    if (host === localHost || host === tableauHost) {
      return url.pathname.startsWith(sandboxPathSegment);
    }

    return false;
  }

  function isSchemeHostPortEqual(lhs: URL, rhs: URL): boolean {
    return lhs.protocol === rhs.protocol && lhs.hostname === rhs.hostname && lhs.port === rhs.port;
  }

  function isFirstClassUrl(url: URL): boolean {
    return url.protocol === 'tableau:';
  }

  export function normalizeExtensionDialogUrl(baseUrl: URL, dialogUrl: string): URL {
    // The constructor takes a url parameter, and an optional base parameter to use
    // as a base if the url parameter is a relative URL
    // Note: For IE11, we must first normalize the base as a URL, or the URL polyfill can throw
    // If the baseUrl parameter is changed to a string, we must use: new URL(base).toString();

    return new URL(dialogUrl, baseUrl.toString());
  }

  export function isValidDialogUrl(extensionUrl: URL, loadedExtensionUrl: URL, requestedDialog: string, normalizedDialogUrl: URL): boolean {
    // Security requirements first class URLs
    //  1. The requestedDialogUrl must be relative
    // Security requirements for absolute URLs
    //  1. The parent needs to be normalized for sandbox testing (happens as part of URL creation)
    //  2. The extension cannot be sandboxed
    //  3. The requestedDialogUrl must resolve to a URL with the same scheme+host+port
    // Security requirements for any relative URLs
    //  1. The parent needs to be normalized for sandbox testing and base directory comparison
    //  2. The requestedDialogUrl must not start with "/"
    //  3. The requestedDialogUrl must resolve to a be in, or a child of, the base extension directory
    // Without these requirements, a URL could be formed to an alternate/non-sandboxed location on the hosting server.

    const isRequestRelative: boolean = isRelative(requestedDialog);

    if (isFirstClassUrl(extensionUrl) && !isRequestRelative) {
      return false;
    }

    if (!isRequestRelative) {
      return isSandboxedExtensionUrl(extensionUrl) ? false : isSchemeHostPortEqual(normalizedDialogUrl, loadedExtensionUrl);
    }

    // Verify relative URL requirements...
    if (requestedDialog.startsWith('/')) {
      return false;
    }

    if (!isSchemeHostPortEqual(normalizedDialogUrl, loadedExtensionUrl)) {
      return false;
    }

    const parentPath: string = loadedExtensionUrl.pathname;
    const dialogPath: string = normalizedDialogUrl.pathname;

    const indexFinalSeparator = parentPath.lastIndexOf('/');
    const parentBase: string = parentPath.substring(0, indexFinalSeparator + 1);
    return dialogPath.startsWith(parentBase);
  }
}
